import type { ThemeChoices } from "$/lib/types/General.types";
import type { KeyValueObject } from "$/lib/types/Graphs.types";
import {
  generateGraphDateRangeFromData,
  generateXAxisTicks,
  generateYAxisTicks,
  handleGenerateYearReferences,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getMinMax } from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import type { ReactNode } from "@tanstack/react-router";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  Brush,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CurveType } from "recharts/types/shape/Curve";
import { customColors } from "tailwind.config";
import Flexbox from "../../common/Flexbox";
import type { GraphFilterItemsProps } from "../GraphFilter/GraphFilterItem";
import CustomTooltip from "./CustomTooltip";
import "./styles.scss";

type Props = {
  syncId?: string;
  withBrush?: boolean;
  data: KeyValueObject[];
  title?: string;
  rightHeaderComponent?: ReactNode;
  filtersComponent?: ReactNode;
  unit?: string;
  graphVisibility?: GraphFilterItemsProps[];
  xAxisDataKey: string;
  colors?: Record<string, string>;
  hasTickFormatter?: boolean;
  theme: ThemeChoices;
  extraData?: unknown[];
  keys: string[];
  extraKeys?: { [x: string]: { dataKey: string } };
  curveType?: CurveType;
  yAxisLabel?: string;
  labelPosition?: string;
  height?: string | number;
  heightPercent?: number;
  yAxisTicksNumber?: number;
  leftMargin?: number;
  hasCustomXAxisDomain?: boolean;
  hasReferences?: boolean;
  formatStr?: string;
  indices?: { start: number; end: number };
  setIndices?: Dispatch<
    SetStateAction<{ start: number; end: number } | undefined>
  >;
  domain?: [number, number];
  hasDomain?: boolean;
  displayNames?: Record<string, string>;
};

const LineGraph = ({
  syncId,
  withBrush = false,
  data,
  title,
  rightHeaderComponent,
  filtersComponent,
  unit = " €/MWh",
  graphVisibility,
  xAxisDataKey,
  colors,
  hasTickFormatter = true,
  theme,
  extraKeys,
  keys,
  curveType = "monotone",
  yAxisLabel,
  labelPosition = "center",
  // height = 500,
  // heightPercent,
  yAxisTicksNumber,
  leftMargin = 10,
  formatStr = "dd/MM/yy",
  indices,
  setIndices,
  hasCustomXAxisDomain = true,
  hasReferences = true,
  domain,
  hasDomain = true,
  displayNames,
}: Props) => {
  // const { height: screenHeight } = useScreen();

  // const [graphHeight, setGraphHeight] = useState<number | undefined>(+height);

  // const graphHeight = useMemo(
  //   () =>
  //     heightPercent
  //       ? Math.round(screenHeight * CHART_HEIGHT_PERCENTAGE) *
  //         (heightPercent / 100)
  //       : height,
  //   [heightPercent, screenHeight, height],
  // );

  // useEffect(() => {
  //   const handleResize = () => {
  //     const container = document.querySelector<HTMLElement>(
  //       "#elec-future-products",
  //     );

  //     if (container) {
  //       const containerHeight = container.clientHeight - 32;

  //       if (heightPercent) {
  //         setGraphHeight(containerHeight * (heightPercent / 100));
  //         return;
  //       }
  //     }

  //     setGraphHeight(typeof height === "string" ? parseFloat(height) : +height);
  //   };

  //   handleResize();

  //   window.addEventListener("resize", handleResize);

  //   return () => {
  //     window.removeEventListener("resize", handleResize);
  //   };
  // }, [heightPercent, height, setGraphHeight]);

  const containerRef = useRef<HTMLDivElement>(null);

  const [, setActiveIndex] = useState<number | null>(null);

  const [brushWidth, setBrushWidth] = useState(0);

  const visibleKeys = useMemo(
    () =>
      graphVisibility
        ? graphVisibility
        : keys.map((key) => ({
            name: key,
            isVisible: true,
          })),
    [graphVisibility, keys],
  );

  const hideKeys = useMemo(
    () =>
      visibleKeys.flatMap<string>((item) => {
        if (item.isVisible === false) {
          const extra = extraKeys?.[item.name]?.dataKey;
          return extra ? [item.name, extra] : [item.name];
        }
        return [];
      }),
    [extraKeys, visibleKeys],
  );

  const filteredData = useMemo(
    () => (indices ? data.slice(indices.start, indices.end + 1) : data),
    [data, indices],
  );

  const defaultDomain = useMemo(() => {
    const minMax = getMinMax(filteredData, keys);

    return [minMax.min.value, minMax.max.value];
  }, [filteredData, keys]);

  const references = useMemo(
    () =>
      hasReferences
        ? handleGenerateYearReferences(data, xAxisDataKey, formatStr)
        : [],
    [data, formatStr, xAxisDataKey, hasReferences],
  );

  const dateRange = useMemo(
    () =>
      hasCustomXAxisDomain
        ? generateGraphDateRangeFromData(filteredData, formatStr, xAxisDataKey)
        : undefined,
    [filteredData, formatStr, xAxisDataKey, hasCustomXAxisDomain],
  );

  const xDomain = useMemo(
    () =>
      hasCustomXAxisDomain && dateRange && dateRange[0] && dateRange[1]
        ? generateXAxisTicks(dateRange[0], dateRange[1])
        : undefined,
    [dateRange, hasCustomXAxisDomain],
  );

  // const notVisibleKeys = (graphVisibility ?? [])
  //   .filter((item) => !item.isVisible && item.isVisible !== undefined)
  //   .map((item) => item.name);

  useEffect(() => {
    if (containerRef.current) {
      setBrushWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef]);

  return (
    <Flexbox fullWidth fullHeight row className="gap-1">
      <Flexbox fullWidth fullHeight className="relative gap-2">
        <Flexbox row fullWidth align="center" justify="between">
          <h2
            className={cn("text-sm font-bold", {
              "text-secondary": theme === "blue",
              "text-forest": theme === "green",
              "text-warning": theme === "orange",
            })}
          >
            {title}
          </h2>
          {rightHeaderComponent}
        </Flexbox>

        <ResponsiveContainer
          ref={containerRef}
          width="100%"
          minHeight={100}
          onResize={() =>
            containerRef.current
              ? setBrushWidth(containerRef.current.offsetWidth)
              : {}
          }
        >
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: leftMargin, bottom: 5 }}
            onMouseLeave={() => setActiveIndex(null)}
            syncId={syncId}
          >
            {references?.map(({ value, label }) => (
              <ReferenceLine
                key={value}
                x={label}
                stroke={customColors.tertiary.DEFAULT}
                strokeDasharray={5}
              />
            ))}

            <ReferenceLine
              y={0}
              stroke={customColors.tertiary.DEFAULT}
              strokeDasharray={5}
            />

            <XAxis
              dataKey={xAxisDataKey}
              fontSize={12}
              interval={hasCustomXAxisDomain ? 0 : "preserveStartEnd"}
              tickMargin={5}
              ticks={xDomain}
            />

            <YAxis
              unit={unit}
              domain={hasDomain ? (domain ?? defaultDomain) : undefined}
              tickSize={2}
              fontWeight={500}
              tickMargin={5}
              width={85}
              fontSize={12}
              tickFormatter={hasTickFormatter ? numberFormatter : undefined}
              interval={0}
              ticks={
                hasDomain
                  ? Array.isArray(domain ?? defaultDomain)
                    ? generateYAxisTicks(
                        Number((domain ?? defaultDomain)[0]),
                        Number((domain ?? defaultDomain)[1]),
                        yAxisTicksNumber,
                      )
                    : undefined
                  : undefined
              }
              label={{
                value: yAxisLabel,
                angle: -90,
                position: labelPosition,
              }}
            />

            <Tooltip
              content={
                <CustomTooltip
                  unit={unit}
                  theme={theme}
                  hideKeys={hideKeys}
                  displayNames={displayNames}
                />
              }
            />

            {visibleKeys.map((item) => (
              <Line
                key={"line-" + item.name}
                type={curveType}
                dataKey={item.name}
                stroke={
                  colors && colors[item.name]
                    ? colors[item.name]
                    : customColors.primary.DEFAULT
                }
                dot={false}
                activeDot={false}
                strokeWidth={
                  item.isVisible === undefined || item.isVisible ? 2 : 0
                }
                connectNulls
              />
            ))}

            {/* {visibleKeys.map((key) => (
              <Line
                key={"line-" + key}
                type={curveType}
                dataKey={key}
                stroke={
                  colors && colors[key]
                    ? colors[key]
                    : customColors.primary.DEFAULT
                }
                dot={false}
                activeDot={false}
                strokeWidth={2}
              />
            ))} */}

            {extraKeys &&
              visibleKeys.map((item) => (
                <Line
                  key={"extra-line-" + item.name}
                  type={curveType}
                  dataKey={extraKeys[item.name]?.dataKey}
                  stroke={
                    colors && colors[item.name]
                      ? colors[item.name]
                      : customColors.primary.DEFAULT
                  }
                  dot={false}
                  activeDot={false}
                  strokeDasharray={"5 5"}
                  strokeWidth={
                    item.isVisible === undefined || item.isVisible ? 2 : 0
                  }
                  connectNulls
                />
              ))}

            {/* {graphVisibility &&
              notVisibleKeys.map((key) => (
                <Line
                  key={"non-line-" + key}
                  type={curveType}
                  dataKey={key}
                  stroke={customColors.tertiary.DEFAULT}
                  strokeOpacity={0.5}
                  dot={false}
                  activeDot={false}
                />
              ))} */}

            {/* {extraKeys &&
              graphVisibility &&
              notVisibleKeys.map((key) => (
                <Line
                  key={"extra-non-line-" + key}
                  type={curveType}
                  dataKey={extraKeys[key]?.dataKey}
                  stroke={customColors.tertiary.DEFAULT}
                  strokeOpacity={0.5}
                  dot={false}
                  activeDot={false}
                  strokeDasharray={"5 5"}
                />
              ))} */}

            {withBrush && (
              <Brush
                stroke={
                  theme === "blue"
                    ? customColors.primary.DEFAULT
                    : theme === "green"
                      ? customColors.forest.DEFAULT
                      : customColors.warning.DEFAULT
                }
                height={16}
                alwaysShowText
                x={brushWidth * 0.18}
                width={brushWidth * 0.7}
                tickFormatter={(value, index) => {
                  return String(data[index]?.[xAxisDataKey] ?? "");
                }}
                onChange={(e) => {
                  if (setIndices) {
                    if (!e.startIndex || !e.endIndex) {
                      setIndices(undefined);
                      return;
                    }

                    setIndices({
                      start: e.startIndex,
                      end: e.endIndex,
                    });
                  }
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Flexbox>

      {filtersComponent}
    </Flexbox>
  );
};

export default LineGraph;
